import { z } from 'zod';
import { dayNamesAndEveryday } from './dayNames';
import { SchedulingConfigurationDTOSchema } from './schedulingConfiguration.dto';

export const FixedTriggerSchema = z.object({
  mode: z.literal('FIXED_AT'),
  at: z
    .string()
    .time()
    .describe('The time of the day the questionnaire will be sent'),
  days: z
    .array(z.enum(dayNamesAndEveryday))
    .describe('The days of the week the questionnaire will be sent'),
});

export const RandomBetweenTriggerSchema = z.object({
  mode: z.literal('RANDOM_BETWEEN'),
  start: z
    .string()
    .time()
    .describe(
      'The start time of the random time range the questionnaire will be sent',
    ),
  end: z
    .string()
    .time()
    .describe(
      'The end time of the random time range the questionnaire will be sent',
    ),
  days: z
    .array(z.enum(dayNamesAndEveryday))
    .describe('The days of the week the questionnaire will be sent'),
});

export const TriggerSchema = z
  .discriminatedUnion('mode', [FixedTriggerSchema, RandomBetweenTriggerSchema])
  .describe('Define at what time of the day the questionnaire will be sent')
  .refine(
    (trigger) => {
      // TODO : discriminatedUnion breaks .refine(), fixed in zod v4 (https://github.com/colinhacks/zod/issues/2440)
      if (trigger.mode === 'RANDOM_BETWEEN') {
        return trigger.start < trigger.end;
      }
      return true;
    },
    { message: 'Random trigger end time must be after start time' },
  );

export type Trigger = z.infer<typeof TriggerSchema>;
export const RemindersSchema = z.object({
  maxAttempts: z
    .number()
    .int()
    .nonnegative()
    .describe(
      'The number of attempts the participant will have to answer the questionnaire',
    ),
  intervalMinutes: z
    .number()
    .int()
    .positive()
    .describe('The interval between attempts'),
  message: z.string().describe('The message to send to the participant'),
});

export type Reminders = z.infer<typeof RemindersSchema>;

export const QuestionnaireScheduleSchema = z.object({
  period: z.object({
    durationDays: z
      .number()
      .int()
      .positive()
      .describe(
        'The number of days the questionnaire will be sent after the participant is enrolled',
      ),
  }),
  triggers: z.array(TriggerSchema),
  answer: z.object({
    validForMinutes: z
      .number()
      .int()
      .positive()
      .describe(
        'The number of minutes the participant will have to answer the questionnaire',
      ),
    reminders: RemindersSchema,
  }),
});

export type QuestionnaireSchedule = z.infer<typeof QuestionnaireScheduleSchema>;

export const QuestionnaireSchedulingSchema = z
  .object({
    day: z
      .enum(dayNamesAndEveryday)
      .describe(
        'The day of the week the questionnaire will be sent, or every day',
      ),
    startTime: z
      .string()
      .time()
      .describe('The time of the day the questionnaire will be sent'),
    endTime: z
      .string()
      .time()
      .nullable()
      .optional()
      .describe(
        'If defined, the questionnaire will be sent at a random time between startTime and endTime',
      ),
  })
  .refine(
    (data) => {
      if (data.endTime) {
        return data.startTime <= data.endTime;
      }
      return true;
    },
    { message: 'Notification end time cannot be before start time' },
  );

export const CreateSchedulingsForQuestionnaireDTOSchema = z.object({
  questionnaireId: z.string().uuid(),
  schedulings: z.array(QuestionnaireSchedulingSchema),
  configuration: SchedulingConfigurationDTOSchema,
});

export type QuestionnaireSchedulingDTO = z.infer<
  typeof QuestionnaireSchedulingSchema
>;
export type CreateSchedulingsForQuestionnaireDTO = z.infer<
  typeof CreateSchedulingsForQuestionnaireDTOSchema
>;
